import { defineStore } from "pinia";
import axios from "axios";

export const useClientStore = defineStore("clientStore", {
  state: () => ({
    showClientModal: false,
    selectedClient: null,
    clients: [],
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
    searchQuery: "",
    loading: false,
    clientAppointments: [],
    clientNotes: [],
    // Appointment pagination
    appointmentsCurrentPage: 1,
    appointmentsLastPage: 1,
    appointmentsTotal: 0,
    appointmentsPerPage: 10,
    // Notes pagination
    notesCurrentPage: 1,
    notesLastPage: 1,
    notesTotal: 0,
    notesPerPage: 10,
  }),
  actions: {
    openClientModal() {
      this.showClientModal = true;
      this.loadClients();
    },
    closeClientModal() {
      this.showClientModal = false;
      this.selectedClient = null;
      this.clientAppointments = [];
      this.clientNotes = [];
      this.appointmentsCurrentPage = 1;
      this.notesCurrentPage = 1;
    },
    async loadClients(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get("/api/customer", {
          params: {
            page: page,
            per_page: this.perPage,
            search: this.searchQuery,
          },
        });
        this.clients = response.data.data;
        this.currentPage = response.data.current_page;
        this.lastPage = response.data.last_page;
        this.total = response.data.total;
      } catch (error) {
        console.error("Error loading clients:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadClientDetails(clientId, appointmentsPage = 1) {
      this.loading = true;
      try {
        const response = await axios.get(`/api/customer/${clientId}`, {
          params: {
            appointments_per_page: this.appointmentsPerPage,
            appointments_page: appointmentsPage,
          },
        });
        this.selectedClient = response.data.customer;
        this.clientAppointments = response.data.appointments.data;
        this.appointmentsCurrentPage = response.data.appointments.current_page;
        this.appointmentsLastPage = response.data.appointments.last_page;
        this.appointmentsTotal = response.data.appointments.total;
        await this.loadClientNotes(clientId);
      } catch (error) {
        console.error("Error loading client details:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadClientNotes(clientId, page = 1) {
      const apiURL = import.meta.env.VITE_API_URL;
      console.log('Loading notes for client:', clientId, 'API URL:', apiURL);
      try {
        const response = await axios.get(apiURL + "notes", {
          params: {
            salon_client_id: clientId,
            page: page,
            per_page: this.notesPerPage,
          }
        });
        console.log('Notes API response:', response.data);

        // Handle paginated response
        if (response.data.notes) {
          const notesData = response.data.notes;

          // Check if it's paginated data
          if (notesData.data) {
            this.clientNotes = notesData.data.sort((a, b) => {
              return new Date(b.created_at) - new Date(a.created_at);
            });
            this.notesCurrentPage = notesData.current_page || 1;
            this.notesLastPage = notesData.last_page || 1;
            this.notesTotal = notesData.total || 0;
          } else {
            // Not paginated, just an array
            this.clientNotes = notesData.sort((a, b) => {
              return new Date(b.created_at) - new Date(a.created_at);
            });
            this.notesCurrentPage = 1;
            this.notesLastPage = 1;
            this.notesTotal = notesData.length;
          }
        } else {
          this.clientNotes = [];
          this.notesCurrentPage = 1;
          this.notesLastPage = 1;
          this.notesTotal = 0;
        }

        console.log('Loaded notes:', this.clientNotes);
      } catch (error) {
        console.error("Error loading client notes:", error);
        this.clientNotes = [];
        this.notesCurrentPage = 1;
        this.notesLastPage = 1;
        this.notesTotal = 0;
      }
    },
    async updateClient(clientId, clientData) {
      try {
        const response = await axios.put(`/api/customer/${clientId}`, clientData);
        this.selectedClient = response.data.customer;
        // Refresh the client list
        await this.loadClients(this.currentPage);
        return response.data;
      } catch (error) {
        console.error("Error updating client:", error);
        throw error;
      }
    },
    setSearchQuery(query) {
      this.searchQuery = query;
      this.currentPage = 1;
      this.loadClients(1);
    },
    nextPage() {
      if (this.currentPage < this.lastPage) {
        this.loadClients(this.currentPage + 1);
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.loadClients(this.currentPage - 1);
      }
    },
    nextAppointmentsPage() {
      if (this.appointmentsCurrentPage < this.appointmentsLastPage) {
        this.loadClientDetails(this.selectedClient.id, this.appointmentsCurrentPage + 1);
      }
    },
    prevAppointmentsPage() {
      if (this.appointmentsCurrentPage > 1) {
        this.loadClientDetails(this.selectedClient.id, this.appointmentsCurrentPage - 1);
      }
    },
    nextNotesPage() {
      if (this.notesCurrentPage < this.notesLastPage) {
        this.loadClientNotes(this.selectedClient.id, this.notesCurrentPage + 1);
      }
    },
    prevNotesPage() {
      if (this.notesCurrentPage > 1) {
        this.loadClientNotes(this.selectedClient.id, this.notesCurrentPage - 1);
      }
    },
  },
});
